import { useState } from "react"
import styled from "styled-components"

import { colors, Text } from "@minuthq/meatball-ui-react"

import { EditBuildingHomeDialog } from "src/components/HomeGroups/HomeGroupBuilding/EditBuildingHomeDialog"
import { HomeAddress } from "src/components/Homes/HomeDetails/HomeSettings/HomeAddress"
import { HomeMonitoringPresets } from "src/components/Homes/HomeDetails/HomeSettings/HomeMonitoringPresets"
import { RemoveHome } from "src/components/Homes/HomeDetails/Overview/RemoveHome"
import {
  SettingContainer,
  TSettingContainerOnSaveReturnType,
} from "src/components/Settings/Setting/SettingContainer"
import { useIsHomeGroupAdmin } from "src/data/homeGroups/logic/useIsHomeGroupAdmin"
import { THome, THomeType } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePatchHome } from "src/data/organizations/queries/homeQueries"
import { getAccessLogic } from "src/data/user/logic/accessLogic"
import { langKeys } from "src/i18n/langKeys"
import { TTranslateFunction, useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { TextButton } from "src/ui/Button/TextButton"
import { Divider } from "src/ui/Divider/Divider"
import ErrorLogIcon from "src/ui/icons/eventIcons/error-log.svg"
import { spacing } from "src/ui/spacing"

export function HomeSettings({ home }: { home: THome }) {
  const [editHomeDialogOpen, setEditHomeDialogOpen] = useState(false)

  const { navigate } = useRouter()
  const { t, langKeys } = useTranslate()

  const { org } = useOrganization()

  const { ready, value } = useIsHomeGroupAdmin()
  const isHomeGroupAdmin = ready && value
  const patchHome = usePatchHome()

  const access = getAccessLogic({ role: org.user_role })
  const homeAccess = getAccessLogic({ role: home.user_role })

  const canRemoveOrEditHome = access.hasAdminAccess || isHomeGroupAdmin

  if (!home) {
    return <div>"Could not find the home.."</div>
  }

  async function handleSubmit({
    spaceUseType,
    floorNumber,
    apartmentNumber,
  }: {
    spaceUseType?: THomeType
    floorNumber?: string
    apartmentNumber?: string
  }): TSettingContainerOnSaveReturnType {
    try {
      await patchHome.mutateAsync({
        homeId: home.home_id,
        orgId: org.id,
        data: {
          space_use_type: spaceUseType,
          address: {
            street_name1: home.address?.street_name1 || "",
            floor_number: floorNumber,
            apartment_number: apartmentNumber,
            post_code: home.address?.post_code || "",
            country: home.address?.country || "",
            city: home.address?.city || "",
          },
        },
      })
      setEditHomeDialogOpen(false)

      return {
        isSuccess: true,
      }
    } catch (error) {
      return {
        isSuccess: false,
      }
    }
  }

  return (
    <HomeSettingsBox>
      <Text variant="heading2">{t(langKeys.settings)}</Text>

      <HomeMonitoringPresets
        homeId={home.home_id}
        activeNoisePresetId={home.noise_profile_id}
        activeICMPresetId={home.icm_profile_id}
      />
      <Divider $margin={spacing.L} />

      <SettingContainer
        title={t(langKeys.home_type)}
        onSave={() => Promise.resolve({ isSuccess: true })}
        inlineEdit={true}
        InputComponent={({ saving }) => (
          <TextButton
            onClick={() => setEditHomeDialogOpen(true)}
            disabled={saving}
          >
            {t(langKeys.edit)}
          </TextButton>
        )}
        displayValue={null}
        description={
          <Text variant="body">
            {home.space_use_type ? (
              parseHomeType(home.space_use_type, t)
            ) : (
              <Text color="emergency">
                <IconWrapper>
                  <ErrorLogIcon width={18} color={colors.systemErrorDark} />
                  {t(langKeys.missing)}
                </IconWrapper>
              </Text>
            )}
          </Text>
        }
        disabled={!canRemoveOrEditHome}
      />

      <EditBuildingHomeDialog
        home={home}
        open={!!editHomeDialogOpen}
        onClose={() => setEditHomeDialogOpen(false)}
        title={t(langKeys.home_group_building_edit_unit_details_title, {
          name: home.name,
        })}
        description={t(
          langKeys.home_group_building_edit_unit_details_description
        )}
        onSubmit={(values) =>
          handleSubmit({
            spaceUseType: values.space_use_type,
            floorNumber: values.floor_number,
            apartmentNumber: values.apartment_number,
          })
        }
        confirmLabel={t(langKeys.save)}
        loading={patchHome.isLoading}
        error={patchHome.error?.message ?? null}
      />

      <Divider $margin={spacing.L} />

      <HomeAddress
        homeId={home.home_id}
        location={home.location}
        address={home.address}
        editable={homeAccess.hasAdminAccess}
      />

      <Divider $margin={spacing.L} />

      <RemoveHome
        homeId={home.home_id}
        homeName={home.name}
        disabled={!canRemoveOrEditHome}
        onRemoveSuccess={() => navigate(Routes.Homes.location())}
      />
    </HomeSettingsBox>
  )
}

function parseHomeType(
  homeType: NonNullable<THomeType>,
  t: TTranslateFunction
) {
  const parsedHomeTypes: Record<NonNullable<THomeType>, string> = {
    private: t(langKeys.home_group_building_create_home_private_area_title),
    communal: t(langKeys.home_group_building_create_home_communal_area_title),
  }

  return parsedHomeTypes[homeType]
}

const HomeSettingsBox = styled.div`
  display: grid;
  max-width: 600px;
  grid-gap: ${spacing.M};
`

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`
